import React, { useState, useEffect, useCallback } from "react";
import { AlertTriangle } from "lucide-react";
import "./AngelInvestor.css";
import investorAvatar from "../assets/investor-avatar.png";
import { GAME_FORMULAS } from "../lib/gameLogic";

// Tutorial sequence with messages and triggers
const TUTORIAL_SEQUENCE = [
  {
    id: "initial_greeting",
    message:
      "Hi! I'm Sarah, and I see huge potential in your platform. I've invested in dozens of successful startups, and I'd love to help guide you to success!",
    condition: (state) => state.isLaunched && !state.tutorialStarted,
    delay: 3000,
    action: {
      type: "SET_FLAG",
      flag: "tutorialStarted",
    },
  },
  {
    id: "introduce_metrics",
    message:
      "You're gonna need to keep track of some stuff to make this work. Let me show you what to watch.",
    condition: (state) => state.tutorialStarted && !state.showMetrics,
    delay: 1000,
    action: {
      type: "SHOW_METRICS",
    },
  },
  {
    id: "explain_metrics",
    message:
      "Right now you're not making any money - which is fine for a new platform! But if you want to turn this into a real business, you might want to try introducing some ads.",
    condition: (state) => state.showMetrics && !state.showControls,
    delay: 1000,
    action: {
      type: "SHOW_CONTROLS",
    },
  },
  {
    id: "first_goal",
    message:
      "If you can make $1,000 with this app, we might be in business. But be careful - don't get too greedy or you'll lose users, and that's no good for anyone.",
    condition: (state) => state.showControls && !state.firstGoalGiven,
    delay: 1000,
    action: {
      type: "SET_FLAG",
      flag: "firstGoalGiven",
    },
  },
  {
    id: "reached_money_goal",
    message:
      "You've really got something here! You might want to invest that money in improving your algorithm - it'll help people stay engaged and get more users on the platform. Plus, better algorithms mean more personalized ads, which means more revenue!",
    condition: (state) =>
      state.money >= 1000 &&
      state.firstGoalGiven &&
      !state.showAlgorithmUpgrade,
    delay: 0,
    action: {
      type: "SHOW_ALGORITHM_UPGRADE",
    },
  },
  {
    id: "algorithm_first_upgrade",
    message:
      "Nice work improving that algorithm! Just remember - running sophisticated algorithms isn't cheap. You can always switch back to a lower level to save some cash. And hey, the more research you do, the cheaper those earlier levels become to run!",
    condition: (state) =>
      state.algorithmLevel >= 2 &&
      state.maxAlgorithmLevel === 2 &&
      !state.shownAlgorithmMessage,
    delay: 0,
    action: {
      type: "SET_FLAG",
      flag: "shownAlgorithmMessage",
    },
  },
  {
    id: "investment_offer",
    message:
      "Now we're talking business! I'm willing to invest in your platform, which means you can run a deficit... but only up to your total user count. If you lose more dollars than you have users, I'll have to pull my investment.",
    condition: (state) =>
      state.algorithmLevel >= 2 &&
      state.maxAlgorithmLevel >= 3 &&
      !state.shownInvestmentOffer,
    delay: 0,
    action: {
      type: "SET_FLAG",
      flag: "shownInvestmentOffer",
    },
  },
  {
    id: "win_condition",
    message:
      "Now, let me tell you what success looks like: we need to reach $1,000,000 in the bank, 100,000 users, and a level 10 algorithm. I'll keep track of these goals for you. When we reach them, we can cash out for a big win!",
    condition: (state) => state.shownInvestmentOffer && !state.showGoals,
    delay: 1000,
    action: {
      type: "SHOW_GOALS",
    },
  },
  {
    id: "personalization_intro",
    message:
      "With this advanced algorithm, you can start personalizing those ads - but tread carefully. Users might not love knowing just how much you know about them...",
    condition: (state) =>
      state.algorithmLevel >= 5 && !state.showAdPersonalization,
    delay: 0,
    action: {
      type: "SHOW_PERSONALIZATION",
    },
  },
];

// Warning conditions that can trigger after tutorial
const WARNINGS = {
  RAPID_USER_LOSS: {
    condition: (state) => {
      const newReputation = GAME_FORMULAS.calculateReputation(
        state.algorithmLevel,
        state.adPersonalization,
        state.adFrequency
      );
      const userGrowth = GAME_FORMULAS.calculateUserGrowth(
        state.userCount,
        newReputation
      );
      return userGrowth / state.userCount < -0.01;
    },
    message:
      "Watch out! You're losing more than 1% of your users per second. You need to stabilize this quickly!",
    cooldown: 30000,
  },
  LOW_USER_COUNT: {
    condition: (state) => state.userCount < 1000 && state.userCount > 0,
    message:
      "Your user base has dropped below 1,000. This is concerning - we need to turn this around!",
    cooldown: 45000,
  },
  DANGEROUS_DEFICIT: {
    condition: (state) => {
      const revenue = GAME_FORMULAS.calculateRevenue(
        state.userCount,
        state.engagement,
        state.adFrequency,
        state.adPersonalization
      );
      const overheadCost = GAME_FORMULAS.calculateOverheadCost(
        state.userCount,
        state.engagement,
        state.algorithmLevel,
        state.maxAlgorithmLevel
      );
      const moneyChangePerTick = revenue - overheadCost;

      // Project 10 ticks into the future
      const projectedMoney = state.money + 10 * moneyChangePerTick;

      return projectedMoney < -state.userCount;
    },
    message:
      "Warning! At this rate, you'll exceed your maximum deficit in just a few seconds. Cut costs or increase revenue immediately!",
    cooldown: 20000,
  },
  LOW_ENGAGEMENT: {
    condition: (state) => state.engagement < 0.3,
    message:
      "User engagement is dropping. Consider upgrading your algorithm or reducing ad frequency.",
    cooldown: 30000,
  },
  HIGH_PERSONALIZATION: {
    condition: (state) => state.adPersonalization > 80,
    message:
      "Users are getting concerned about privacy. Maybe ease up on the personalization?",
    cooldown: 30000,
  },
};

// Game over messages
const GAME_OVER_MESSAGES = {
  deficit: {
    title: "Investment Withdrawn",
    message:
      "I'm sorry, but your deficit has exceeded your user count. I have to pull my investment. Game Over.",
  },
  users: {
    title: "Platform Collapse",
    message:
      "With no users left on the platform, there's nothing more we can do here. Game Over.",
  },
  win: {
    title: "Congratulations!",
    message:
      "You've reached $1,000,000 in the bank, 100,000 users, and a level 10 algorithm. It's time to sell your company and gloat!",
  },
};

type Message = {
  id: string;
  message: string;
  action?: {
    type: string;
    flag?: string;
  };
  type?: string;
};

const AngelInvestor = ({ gameState, onStateUpdate }) => {
  const [currentMessage, setCurrentMessage] = useState<Message | null>(null);
  const [shownMessages, setShownMessages] = useState(new Set());
  const [lastWarningTimes, setLastWarningTimes] = useState({});
  const [tutorialComplete, setTutorialComplete] = useState(false);

  // Handle game over state
  useEffect(() => {
    if (gameState.gameOver && !currentMessage) {
      const gameOverInfo = GAME_OVER_MESSAGES[gameState.gameOverReason];
      if (gameOverInfo) {
        setCurrentMessage({
          id: "game_over",
          message: gameOverInfo.message,
          type: gameState.gameOverReason === "win" ? "win" : "game_over",
        });
      }
    }
  }, [gameState.gameOver, gameState.gameOverReason, currentMessage]);

  // Function to handle state updates based on angel actions
  const handleAction = useCallback(
    (action) => {
      if (!action) return;

      switch (action.type) {
        case "SET_FLAG":
          onStateUpdate({ [action.flag]: true });
          break;
        case "SHOW_METRICS":
          onStateUpdate({ showMetrics: true });
          break;
        case "SHOW_CONTROLS":
          onStateUpdate({ showControls: true });
          break;
        case "SHOW_ALGORITHM_UPGRADE":
          onStateUpdate({ showAlgorithmUpgrade: true });
          break;
        case "SHOW_PERSONALIZATION":
          onStateUpdate({
            showAdPersonalization: true,
            tutorialComplete: true,
          });
          setTutorialComplete(true);
          break;
        case "SHOW_GOALS":
          onStateUpdate({ showGoals: true });
          break;
        default:
          break;
      }
    },
    [onStateUpdate]
  );

  // Check for next tutorial message
  useEffect(() => {
    if (currentMessage || tutorialComplete) return;

    const nextStep = TUTORIAL_SEQUENCE.find(
      (step) => !shownMessages.has(step.id) && step.condition(gameState)
    );

    if (nextStep) {
      const timer = setTimeout(() => {
        setCurrentMessage({
          id: nextStep.id,
          message: nextStep.message,
          action: nextStep.action,
        });
      }, nextStep.delay);

      return () => clearTimeout(timer);
    }
  }, [gameState, currentMessage, shownMessages, tutorialComplete]);

  // Check for warnings after algorithm message but before tutorial completion
  useEffect(() => {
    if (
      !gameState.shownAlgorithmMessage ||
      currentMessage ||
      gameState.gameOver
    )
      return;

    // Don't show warnings if we haven't seen the algorithm message yet
    for (const [warningId, warning] of Object.entries(WARNINGS)) {
      const lastWarning = lastWarningTimes[warningId] || 0;
      if (
        warning.condition(gameState) &&
        Date.now() - lastWarning > warning.cooldown
      ) {
        setCurrentMessage({
          id: warningId,
          message: warning.message,
          type: "warning",
        });
        setLastWarningTimes((prev) => ({
          ...prev,
          [warningId]: Date.now(),
        }));
        break;
      }
    }
  }, [gameState, tutorialComplete, currentMessage, lastWarningTimes]);

  const handleDismiss = () => {
    if (currentMessage) {
      if (
        currentMessage.type === "game_over" ||
        currentMessage.type === "win"
      ) {
        // Handle both game over and win by restarting
        window.location.reload();
      } else {
        handleAction(currentMessage.action);
        setShownMessages((prev) => new Set([...prev, currentMessage.id]));
        setCurrentMessage(null);
      }
    }
  };

  if (!currentMessage) return null;

  return (
    <div className="angel-investor-container">
      <div className="angel-portrait">
        <img
          src={investorAvatar}
          alt="Angel Investor Sarah"
          className="angel-avatar"
        />
      </div>
      <div
        className={`speech-bubble ${
          currentMessage.type === "game_over"
            ? "game-over"
            : currentMessage.type === "win"
            ? "win"
            : ""
        }`}
      >
        {currentMessage.type === "warning" && (
          <AlertTriangle className="message-icon warning" />
        )}
        {currentMessage.type === "game_over" && (
          <AlertTriangle className="message-icon game-over" />
        )}
        <div className="message-content">
          <p>{currentMessage.message}</p>
          <button
            className={`dismiss-button ${currentMessage.type || "tutorial"}`}
            onClick={handleDismiss}
          >
            {currentMessage.type === "game_over"
              ? "Restart"
              : currentMessage.type === "win"
              ? "Begin Again"
              : "Got it"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AngelInvestor;
